/**
 * EditableFieldBox - iter1102 card-UX overhaul (Feb 2026)
 *
 * A bordered, clearly-editable field for Counterparty and Description on the
 * bank-feed transaction cards. Replaces the previous "buried" muted-italic
 * text that users couldn't tell was editable.
 *
 * Anatomy:
 *   ┌──────────────────────────────────────┐
 *   │ 👤  Apple                        ✎  │
 *   └──────────────────────────────────────┘
 *
 * - Always-visible icon (left) + pencil (right) → obvious click-to-edit
 * - Foreground text weight (not muted) → readable at a glance
 * - Description supports multi-line (up to 3), not aggressive 80-char truncation
 * - Hover ring in brand teal → confirms it's interactive
 * - Popover on click → same edit UX as before, minimal friction
 */
import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Edit3, Check, X, Loader2 } from 'lucide-react';

export const EditableFieldBox = ({
  icon: Icon,              // Lucide icon component for the left slot
  value,
  placeholder,
  label,                   // "counterparty" / "description" — used in popover title
  multiline = false,       // true → textarea + up to 3-line preview
  maxLength = 200,
  onSave,
  disabled = false,
  testIdPrefix,
}) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value || ''); }, [value]);

  const commit = async () => {
    const next = (val || '').trim();
    if (next === (value || '').trim()) { setOpen(false); return; }
    setSaving(true);
    try { await onSave?.(next); setOpen(false); }
    catch { /* toast upstream */ }
    finally { setSaving(false); }
  };

  const hasValue = !!(value && String(value).trim());
  // Multi-line clamp for description; single-line truncate for counterparty
  const textClasses = multiline
    ? 'text-[13px] leading-snug text-foreground line-clamp-2 whitespace-pre-wrap break-words'
    : 'text-[13px] leading-snug text-foreground truncate font-medium';

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      className={[
        'group w-full text-left flex items-start gap-2 rounded-lg border transition-all',
        'px-2 py-1.5 min-h-[34px]',
        disabled
          ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-70'
          : 'border-slate-200 bg-white hover:border-[#108981] hover:ring-1 hover:ring-[#108981]/30 focus:outline-none focus:ring-2 focus:ring-[#108981]/40 active:bg-[#108981]/5',
      ].join(' ')}
      title={disabled ? '' : `Click to edit ${label}`}
      data-testid={testIdPrefix ? `${testIdPrefix}-edit` : undefined}
    >
      {Icon && (
        <Icon
          className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${hasValue ? 'text-[#108981]' : 'text-slate-400'}`}
          aria-hidden="true"
        />
      )}
      <span className="flex-1 min-w-0">
        {hasValue ? (
          <span className={textClasses}>{value}</span>
        ) : (
          <span className="text-[13px] text-slate-400 italic">{placeholder}</span>
        )}
      </span>
      {!disabled && (
        <Edit3
          className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-300 group-hover:text-[#108981] transition-colors"
          aria-hidden="true"
        />
      )}
    </button>
  );

  if (disabled) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="bottom" align="start">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Edit {label}</p>
        {multiline ? (
          <Textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            maxLength={maxLength}
            rows={3}
            className="text-xs resize-none"
            autoFocus
            placeholder={placeholder}
            data-testid={testIdPrefix ? `${testIdPrefix}-input` : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
            }}
          />
        ) : (
          <Input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            maxLength={maxLength}
            className="h-8 text-xs"
            autoFocus
            placeholder={placeholder}
            data-testid={testIdPrefix ? `${testIdPrefix}-input` : undefined}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setOpen(false); }}
          />
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-muted-foreground">
            {multiline ? 'Cmd/Ctrl+Enter to save' : 'Enter to save'}
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpen(false)} disabled={saving}>
              <X className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              className="h-7 px-3 bg-[#108981] hover:bg-[#0d6f68]"
              onClick={commit}
              disabled={saving}
              data-testid={testIdPrefix ? `${testIdPrefix}-save` : undefined}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EditableFieldBox;
